import { extractSections } from '../audit/reportTextExtract';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import type { PublicSentimentSection } from '../../types/siteEvidence';

export const SOURCE_ADMISSION_GAP_TEXT = 'Finding omitted: its supporting source was unavailable or unread. This evidence gap remains unresolved; no conclusion is established by the omitted passage.';

export type SourceOmissionReason = 'source-unavailable' | 'source-unread' | 'not-independent-evidence';
export interface ReportSourceAdmission {
  /** Original, one-based frozen reference ordinal; filtering never renumbers it. */
  index: number;
  reference: DataSource;
  admitted: boolean;
  reason?: SourceOmissionReason;
}
const hasText = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

export interface SourceAdmissionInput {
  references?: readonly DataSource[];
  sourceCoverage?: NonNullable<SiteRagReport['screeningBasis']>['sourceCoverage'];
  publicSentiment?: Pick<PublicSentimentSection, 'entries' | 'tier1Facts' | 'sourceLeads' | 'fetchedAt'>;
}

/** Availability is distinct from evidentiary strength. Partial, estimated and
 * source-stated inputs keep their actual qualification; no label promotes them.
 * A collection attempt or search discovery alone is never a retrieved passage. */
export function admitEvidenceSources(input: SourceAdmissionInput): ReportSourceAdmission[] {
  return (input.references ?? []).map((reference, offset) => {
    let reason: SourceOmissionReason | undefined;
    if (reference.qualification === 'unavailable') reason = 'source-unavailable';
    // Match the actual receipt identity, not a provider/source-name substring.
    if (!reason && input.sourceCoverage?.some(row => row.status === 'unavailable'
      && row.section === reference.field && row.source === reference.source
      && row.retrievedAt === reference.retrievedAt)) reason = 'source-unavailable';
    // The report-input projection deliberately withholds this exact model
    // context. Keep its private slot, but don't list it as independent evidence.
    if (!reason && reference.field === 'siteResearch' && reference.sourceGrade === 'inference'
      && reference.subItems?.some(row => row.topic === 'Source limitation')) reason = 'not-independent-evidence';
    if (!reason && input.publicSentiment && reference.field.startsWith('publicSentiment')) {
      const entry = (input.publicSentiment.entries ?? []).some(row => hasText(row.sourceId) && hasText(reference.url) && row.url === reference.url
        && (hasText(row.sourcePassage) || hasText(row.supportingSnippet))
        && row.fetchedAt === reference.retrievedAt
        && (!reference.dataAsOf || row.observedDate === reference.dataAsOf)
        && (!reference.documentId || row.documentSource?.documentId === reference.documentId));
      // Normalized tier-1 fetcher payload, not a discovery URL. Require the
      // retained scalar and exact source/vintage/retrieval bindings. This
      // establishes availability only; the reference's qualification survives.
      const fact = (input.publicSentiment.tier1Facts ?? []).some(row =>
        hasText(row.sourceId) && hasText(row.key) && hasText(row.label)
        && (typeof row.value === 'number' ? Number.isFinite(row.value) : hasText(row.value))
        && hasText(row.url) && row.url === reference.url && row.sourceId === reference.source
        && hasText(row.observedAt) && row.observedAt === reference.dataAsOf
        && input.publicSentiment?.fetchedAt === reference.retrievedAt);
      if (!entry && !fact) reason = 'source-unread';
    }
    return { index: offset + 1, reference, admitted: reason === undefined, ...(reason ? { reason } : {}) };
  });
}

/** Frozen-report consumer API. Receipts and exact retained source payloads are
 * used when present; legacy rows are never upgraded or rewritten. Consumers
 * must separately omit a missing Ref N rather than manufacture a source row. */
export function admitReportSources(report: Pick<SiteRagReport,
  'references' | 'screeningBasis' | 'publicSentimentResearch' | 'publicSentimentMeta'>): ReportSourceAdmission[] {
  const section = report.publicSentimentResearch?.section;
  return admitEvidenceSources({ references: report.references,
    sourceCoverage: report.screeningBasis?.sourceCoverage,
    ...(section ? { publicSentiment: section } : {}) });
}

/** A discovered lead is displayable only when an actually retained observation
 * or contextual fact at that exact URL supplied an admitted reference. */
export function readableSentimentLeadUrls(input: SourceAdmissionInput): ReadonlySet<string> {
  if (!input.publicSentiment) return new Set();
  return new Set(admitEvidenceSources(input).filter(row => row.admitted && row.reference.field.startsWith('publicSentiment'))
    .flatMap(row => row.reference.url ? [row.reference.url] : []));
}

/** Readers and native projections preserve the private reference array. A
 * passage supported only by omitted sources becomes an honest gap, never an
 * uncited asserted fact. An existing admitted alternative stays subject to the
 * normal citation/source-role checks; admission itself does not prove support. */
export function projectReportSourceEvidence(report: SiteRagReport, input?: SourceAdmissionInput): SiteRagReport {
  const admissions = input ? admitEvidenceSources(input) : admitReportSources(report);
  const omitted = new Set(admissions.filter(row => !row.admitted).map(row => row.index));
  const sections = extractSections(report);
  // Scan the actual rendered containers as well as flattened prose. Custom
  // Brief risks and typed FindingClaim evidence ordinals are not included in
  // the audit extractor's question/findings/evidence-only projection.
  const roots = new Set(sections.map(row => row.id));
  for (const root of ['markdown', 'sectionContent', 'sections', 'customBriefAnswers',
    'customBriefExecutiveSummary', 'customBriefProjectContext', 'customBriefConclusion']) roots.add(root);
  const scanRendered = (value: unknown): void => {
    if (typeof value === 'string') {
      for (const ref of value.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))) {
        for (const id of ref[0].match(/\d+/g) ?? []) {
          const index = Number(id);
          if (index < 1 || index > admissions.length) omitted.add(index);
        }
      }
    } else if (Array.isArray(value)) value.forEach(scanRendered);
    else if (value && typeof value === 'object') {
      const row = value as Record<string, unknown>;
      if (typeof row.claim === 'string' && ['confirmed', 'likely_present_unverified', 'unknown'].includes(String(row.evidenceState))) {
        const evidence = row.evidence as { refIds?: unknown[] } | undefined;
        for (const id of Array.isArray(evidence?.refIds) ? evidence.refIds : []) {
          if (typeof id === 'number' && (!Number.isInteger(id) || id < 1 || id > admissions.length)) omitted.add(id);
        }
      }
      Object.values(row).forEach(scanRendered);
    }
  };
  const renderedValues = report as unknown as Record<string, unknown>;
  for (const root of roots) if (root !== 'claims' && Object.hasOwn(renderedValues, root)) scanRendered(renderedValues[root]);
  for (const text of [...sections.map(row => row.content), (report as SiteRagReport & { markdown?: string }).markdown ?? '']) {
    for (const ref of text.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))) {
      for (const id of ref[0].match(/\d+/g) ?? []) {
        const index = Number(id);
        if (index < 1 || index > admissions.length) omitted.add(index);
      }
    }
  }
  for (const claim of report.claims ?? []) for (const source of claim.sources ?? []) {
    const match = /^ref:(\d+)$/i.exec(source);
    if (match && (Number(match[1]) < 1 || Number(match[1]) > admissions.length)) omitted.add(Number(match[1]));
  }
  if (!omitted.size) return report;
  const admitted = new Set(admissions.filter(row => row.admitted).map(row => row.index));
  const gap = SOURCE_ADMISSION_GAP_TEXT;
  const projectText = (text: string): string => text.split(/(\n\s*\n)/).map(paragraph => {
    const tokens = Array.from(paragraph.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi')));
    const refs = tokens.flatMap(row => (row[0].match(/\d+/g) ?? []).map(Number));
    if (!refs.some(index => omitted.has(index))) return paragraph;
    // Independent citation groups do not lend support to each other. A
    // separate unavailable-only group can support a different clause, so omit
    // the complete paragraph even when another group has an admitted source.
    if (tokens.some(token => {
      const group = (token[0].match(/\d+/g) ?? []).map(Number);
      return group.some(index => omitted.has(index)) && !group.some(index => admitted.has(index));
    })) return gap;
    return paragraph.replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), token => {
      const inherited = /^(?:\[)(verified|inferred|unverified)\s+Ref/i.exec(token)?.[1];
      return token.slice(1, -1).split(',').flatMap(part => {
        const match = /^\s*(?:(verified|inferred|unverified)\s+)?(?:Ref\s*:?\s*)?(\d+)\s*$/i.exec(part);
        if (!match || omitted.has(Number(match[2]))) return [];
        const qualifier = match[1] ?? inherited;
        return [`[${qualifier ? `${qualifier} ` : ''}Ref ${match[2]}]`];
      }).join(', ');
    });
  }).join('');
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') return projectText(value);
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      const row = value as Record<string, unknown>;
      // A FindingClaim's evidence ordinals are one joint support group. Remove
      // only unavailable members when an admitted alternative remains; an
      // unsupported finding becomes a valid unknown gap, never a confirmed
      // assertion with an empty or phantom documentation chip.
      if (typeof row.claim === 'string' && ['confirmed', 'likely_present_unverified', 'unknown'].includes(String(row.evidenceState))) {
        const evidence = row.evidence && typeof row.evidence === 'object'
          ? row.evidence as Record<string, unknown> : undefined;
        const refIds = Array.isArray(evidence?.refIds) ? evidence.refIds : [];
        const kept = refIds.filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && admitted.has(id));
        const claim = projectText(row.claim);
        const excerpt = typeof evidence?.excerpt === 'string' ? projectText(evidence.excerpt) : undefined;
        if ((refIds.length > 0 && !kept.length) || claim === gap || excerpt === gap) {
          return { claim: gap, evidenceState: 'unknown', openQuestion: 'What retrieved evidence resolves this source gap?' };
        }
        return Object.fromEntries(Object.entries(row).map(([key, child]) => [key,
          key === 'evidence' && evidence ? { ...evidence, refIds: kept,
            ...(excerpt !== undefined ? { excerpt } : {}) } : walk(child)]));
      }
      const explicit = ['sourceIds', 'sources'].flatMap(key => Array.isArray(row[key]) ? row[key] as unknown[] : []);
      const numbered = explicit.filter((source): source is string => typeof source === 'string' && /^ref:\d+$/i.test(source));
      if (numbered.length && numbered.length === explicit.length && numbered.every(source => !admitted.has(Number(source.slice(4))))) return gap;
      if (typeof row.source === 'string' && projectText(row.source) === gap) return gap;
      return Object.fromEntries(Object.entries(row).map(([key, child]) => [key,
        (key === 'sourceIds' || key === 'sources') && Array.isArray(child)
          ? child.filter(source => typeof source !== 'string' || !/^ref:\d+$/i.test(source) || admitted.has(Number(source.slice(4))))
          : walk(child)]));
    }
    return value;
  };
  const out = { ...report };
  const values = out as unknown as Record<string, unknown>;
  // Exact rendered roots, not private source receipts, inputs, quotes or QA.
  for (const root of roots) if (root !== 'claims' && Object.hasOwn(values, root)) values[root] = walk(values[root]);
  // Typed findings keep their required shape when the only source row is
  // omitted; otherwise a separate `detail`/numeric fact can survive a missing
  // `source` field in the generic object renderer.
  if (out.structuredFindings) out.structuredFindings = out.structuredFindings.map(finding =>
    typeof finding === 'string' ? { title: 'Evidence gap', detail: gap, source: 'No admitted supporting source', impact: 'Unresolved' } : finding);
  if (report.claims) out.claims = report.claims.flatMap(claim => {
    const sources = (claim.sources ?? []).filter(source => {
      const index = /^ref:(\d+)$/i.exec(source); return !index || admitted.has(Number(index[1]));
    });
    if (claim.sources?.length && !sources.length) return [];
    return [{ ...claim, text: projectText(claim.text), sources }];
  });
  return out;
}

/** Intersect, never remap: a source hidden by existing appendix policy or by
 * exact availability evidence cannot acquire a clickable body citation. */
export function admittedReportReferenceIndices(report: SiteRagReport, visible?: ReadonlySet<number>): Set<number> {
  return new Set(admitReportSources(report).filter(row => row.admitted && (!visible || visible.has(row.index))).map(row => row.index));
}

/** Availability rows need a retained payload, not a collection success flag.
 * Legacy capped ledger excerpts certify only a partial read, never stance or
 * full coverage. The original coverage/qualification is always displayed. */
export function retainedSentimentSourceIds(report: { publicSentimentResearch?: SiteRagReport['publicSentimentResearch']; publicSentimentMeta?: Pick<NonNullable<SiteRagReport['publicSentimentMeta']>, 'projectionVersion' | 'ledger' | 'tier1Facts'> }): Set<string> {
  const section = report.publicSentimentResearch?.section;
  if (section) return new Set([
    ...section.entries.filter(row => hasText(row.sourceId) && hasText(row.url) && hasText(row.fetchedAt)
      && (hasText(row.sourcePassage) || hasText(row.supportingSnippet))).map(row => row.sourceId),
    ...(section.tier1Facts ?? []).filter(row => hasText(row.sourceId) && hasText(row.key) && hasText(row.label)
      && hasText(row.url) && hasText(row.observedAt)
      && (typeof row.value === 'number' ? Number.isFinite(row.value) : hasText(row.value))).map(row => row.sourceId),
  ]);
  const meta = report.publicSentimentMeta;
  if (meta?.projectionVersion !== 'psr-meta-v2') return new Set();
  return new Set([
    ...(meta.ledger ?? []).filter(row => hasText(row.sourceId) && hasText(row.snippet) && hasText(row.url)).map(row => row.sourceId),
    ...(meta.tier1Facts ?? []).filter(row => hasText(row.sourceId) && hasText(row.key) && hasText(row.label)
      && hasText(row.url) && hasText(row.observedAt)
      && (typeof row.value === 'number' ? Number.isFinite(row.value) : hasText(row.value))).map(row => row.sourceId),
  ]);
}

export function admittedSentimentLeadUrls(report: SiteRagReport): Set<string> {
  if (!report.publicSentimentResearch?.section) return new Set();
  return new Set(admitReportSources(report).filter(row => row.admitted && row.reference.field.startsWith('publicSentiment'))
    .flatMap(row => row.reference.url ? [row.reference.url] : []));
}
